import { UseMutationResult } from "@tanstack/react-query";
import moment from "moment";

import { Iso8601Date } from "../service/server/Types";

export type NetworkRequestState = {
  started: boolean;
  running: boolean;
  success: boolean;
  error: boolean;

  startedAt?: Iso8601Date;
  errorMessage?: string;
  errorStatus?: string;
  successMessage?: string;
};

const unstarted = (): NetworkRequestState => ({
  started: false,
  running: false,
  success: false,
  error: false,
});

const start = (): NetworkRequestState => ({
  started: true,
  startedAt: moment().toISOString(),
  success: false,
  running: true,
  error: false,
});

const success = (original?: NetworkRequestState, message?: string): NetworkRequestState => ({
  started: true,
  startedAt: (original && original.startedAt) || moment().toISOString(),
  success: true,
  running: false,
  error: false,
  successMessage: message,
});

export type MutationOrNetworkState = NetworkRequestState | UseMutationResult;

const failed = (
  original: NetworkRequestState,
  message: string,
  status: string | undefined = undefined,
): NetworkRequestState => ({
  started: true,
  startedAt: (original && original.startedAt) || moment().toISOString(),
  success: false,
  running: false,
  error: true,
  errorMessage: message,
  errorStatus: status,
});

const isFinished = (r: MutationOrNetworkState) => {
  if ((r as NetworkRequestState).running !== undefined) {
    const s = r as NetworkRequestState;
    return s.started && !s.running;
  }

  if ((r as UseMutationResult).isLoading !== undefined) {
    const m = r as UseMutationResult;
    return m.isSuccess || m.isError;
  }

  return false;
};

const isStarted = (r: MutationOrNetworkState) => {
  if ((r as NetworkRequestState).started !== undefined) {
    return (r as NetworkRequestState).started;
  }

  if ((r as UseMutationResult).isLoading !== undefined) {
    const m = r as UseMutationResult;
    return m.isLoading || m.isSuccess || m.isError;
  }

  return false;
};

const isFailed = (r: MutationOrNetworkState) => {
  if ((r as NetworkRequestState).started !== undefined) {
    return (r as NetworkRequestState).error;
  }

  if ((r as UseMutationResult).isLoading !== undefined) {
    return (r as UseMutationResult).isError;
  }

  return false;
};

const isRunning = (r: MutationOrNetworkState) => {
  if ((r as NetworkRequestState).started !== undefined) {
    return (r as NetworkRequestState).running;
  }

  if ((r as UseMutationResult).isLoading !== undefined) {
    // @ts-ignore
    return (r as UseMutationResult).isLoading;
  }

  return false;
};

const areFinished = (...requests: MutationOrNetworkState[]) => requests.every((r) => isFinished(r));
const areUnstarted = (...requests: MutationOrNetworkState[]) =>
  requests.every((r) => !isStarted(r));

const anyFailed = (...requests: MutationOrNetworkState[]) => requests.some((r) => isFailed(r));
const anyRunning = (...requests: MutationOrNetworkState[]) => requests.some((r) => isRunning(r));

export const NetworkRequest = {
  start,
  success,
  failed,
  unstarted,

  isFinished,
  areFinished,
  areUnstarted,
  anyFailed,
  anyRunning,
};
